/**
 * Push notifications: Capacitor (iOS/Android) and web (FCM + service worker).
 *
 * iOS: @capacitor-firebase/messaging
 * Android: @capacitor/push-notifications (FCM tokens)
 * Web: firebase/messaging + VAPID (REACT_APP_FIREBASE_VAPID_KEY)
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import type { Messaging } from 'firebase/messaging';
import { getMessaging, getToken, isSupported, onMessage, deleteToken } from 'firebase/messaging';
import { firebaseManager } from '@watchlist/shared';
import { nativeFirebase } from './nativeFirebaseService';
import {
  navigateFromNotificationWithFallback,
  normalizeNotificationData,
  type NotificationData,
} from './notificationNavigationService';

let currentToken: string | null = null;
/** Dedup guard — prevents writing the same token to Firestore twice in one session
 * (iOS FCM plugin fires tokenReceived AND getToken resolves on the same cold open). */
let _lastSavedToken: string | null = null;
let webMessaging: Messaging | null = null;
let webForegroundListenerAttached = false;
let serviceWorkerPushNavListenerAttached = false;

/** iOS: tap listener must survive init and must register before auth (cold start from push). */
let iosTapListenerAttached = false;
let iosTokenForegroundListenersAttached = false;

/** Last user we registered push for (native) — used to re-save FCM token on app resume (iOS). */
let lastRegisteredNativePushUserId: string | null = null;
let nativeAppResumePushListenerAttached = false;

const SW_PUSH_NAV_TYPE = 'WATCHLISTS_PUSH_NAV';

function attachServiceWorkerPushNavigationListener(): void {
  if (serviceWorkerPushNavListenerAttached) return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  serviceWorkerPushNavListenerAttached = true;
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type !== SW_PUSH_NAV_TYPE || typeof event.data.path !== 'string') return;
    const path = event.data.path.startsWith('/') ? event.data.path : `/${event.data.path}`;
    navigateFromNotificationWithFallback({ tapPath: path } as NotificationData);
  });
}

/**
 * Native always; web when secure context + Notification + service worker APIs exist.
 */
export const isPushSupported = (): boolean => {
  if (Capacitor.isNativePlatform()) {
    return true;
  }
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
};

/**
 * Initialize push after sign-in (native plugins or web FCM).
 */
function attachNativeAppResumePushRefreshOnce(): void {
  if (nativeAppResumePushListenerAttached || typeof window === 'undefined') return;
  nativeAppResumePushListenerAttached = true;
  window.addEventListener('watchlistsAppResumed', () => {
    const uid = lastRegisteredNativePushUserId;
    if (!uid || !Capacitor.isNativePlatform()) return;
    if (Capacitor.getPlatform() === 'ios') {
      void refreshIosPushTokenAfterResume(uid);
    }
  });
}

async function refreshIosPushTokenAfterResume(userId: string): Promise<void> {
  try {
    const res = await FirebaseMessaging.getToken();
    const token = res.token ?? null;
    if (token) {
      currentToken = token;
      await savePushToken(userId, token);
      console.log('iOS: FCM token re-synced after app resume');
    }
  } catch (e) {
    console.warn('iOS: FCM getToken on resume failed:', e);
  }
}

export const initializePushNotifications = async (userId: string): Promise<void> => {
  if (!isPushSupported()) {
    console.log('Push notifications not supported on this platform');
    return;
  }

  if (Capacitor.isNativePlatform()) {
    lastRegisteredNativePushUserId = userId;
    attachNativeAppResumePushRefreshOnce();
    const platform = Capacitor.getPlatform();
    try {
      if (platform === 'ios') {
        await initializeIOSPushNotifications(userId);
      } else {
        await initializeAndroidPushNotifications(userId);
      }
    } catch (error) {
      console.error('Error initializing native push notifications:', error);
    }
    return;
  }

  await initializeWebPushNotifications(userId);
};

const initializeWebPushNotifications = async (userId: string): Promise<void> => {
  attachServiceWorkerPushNavigationListener();
  try {
    if (!(await isSupported())) {
      console.log('Web push: Firebase Messaging is not supported in this browser');
      return;
    }

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim();
    if (!vapidKey) {
      console.warn(
        'Web push: add REACT_APP_FIREBASE_VAPID_KEY to .env.local (Firebase Console → Project settings → Cloud Messaging → Web push certificates)'
      );
      return;
    }

    let registration: ServiceWorkerRegistration;
    if (process.env.NODE_ENV === 'production') {
      registration = await navigator.serviceWorker.ready;
    } else {
      registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Web push: notification permission not granted');
      return;
    }

    const app = await firebaseManager.getApp();
    const messaging = getMessaging(app);
    webMessaging = messaging;

    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: registration });
    if (!token) {
      console.warn('Web push: getToken returned empty');
      return;
    }

    currentToken = token;
    await savePushToken(userId, token);

    if (!webForegroundListenerAttached) {
      webForegroundListenerAttached = true;
      onMessage(messaging, (payload) => {
        handleForegroundNotification({
          id: payload.messageId || '',
          title: payload.notification?.title || '',
          body: payload.notification?.body || '',
          data: (payload.data || {}) as Record<string, string>,
        });
      });
    }

    console.log('Web push: FCM token registered');
  } catch (error) {
    console.error('Web push: initialization failed', error);
  }
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

/**
 * Prefer FCM `data` for routing; some iOS builds stringify `data` or put keys only on the root object.
 */
function extractTapPayloadForNormalization(notification: unknown): unknown {
  if (!notification || typeof notification !== 'object') return notification;
  const n = notification as Record<string, unknown>;
  let data = n.data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      // fall through — normalizeNotificationData will still flatten root keys
    }
  }
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return data;
  }
  return notification;
}

function attachIosNotificationTapListenerOnce(): void {
  if (iosTapListenerAttached) return;
  iosTapListenerAttached = true;
  FirebaseMessaging.addListener('notificationActionPerformed', (event) => {
    console.log('iOS notification action performed:', event);
    handleNotificationTap({
      actionId: event.actionId,
      notification: {
        id: event.notification.id || '',
        title: event.notification.title,
        body: event.notification.body,
        data: event.notification.data
      }
    });
  });
}

function attachIosTokenAndForegroundListeners(userId: string): void {
  if (iosTokenForegroundListenersAttached) return;
  iosTokenForegroundListenersAttached = true;

  FirebaseMessaging.addListener('tokenReceived', async (event) => {
    console.log('iOS FCM token refreshed:', event.token ? `${event.token.substring(0, 24)}…` : '(empty)');
    currentToken = event.token;
    await savePushToken(userId, event.token);
  });

  FirebaseMessaging.addListener('notificationReceived', (event) => {
    console.log('iOS notification received:', event.notification);
    handleForegroundNotification({
      id: event.notification.id || '',
      title: event.notification.title,
      body: event.notification.body,
      data: event.notification.data
    });
  });
}

/**
 * Call from app entry (after Capacitor init) on iOS so notification taps are not lost before auth registers listeners.
 */
export function registerEarlyIosNotificationTapRouting(): void {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
  attachIosNotificationTapListenerOnce();
}

/**
 * iOS: FCM token can arrive slightly after permission/APNs registration; register listeners before getToken
 * and retry getToken so Firestore gets a real token (server only sends to userPushTokens).
 */
const initializeIOSPushNotifications = async (userId: string): Promise<void> => {
  try {
    // Must run before permission prompt so cold-start taps are not dropped while waiting for auth init.
    attachIosNotificationTapListenerOnce();

    const permissionStatus = await FirebaseMessaging.requestPermissions();
    console.log('iOS push permission status:', permissionStatus.receive);

    if (permissionStatus.receive !== 'granted') {
      console.log('iOS push notification permission not granted');
      return;
    }

    attachIosTokenAndForegroundListeners(userId);

    let token: string | null = null;
    for (let attempt = 1; attempt <= 6; attempt += 1) {
      const res = await FirebaseMessaging.getToken();
      token = res.token ?? null;
      if (token) break;
      const delayMs = 350 * attempt;
      console.warn(`iOS FCM getToken empty (attempt ${attempt}/6), retrying in ${delayMs}ms…`);
      await sleep(delayMs);
    }

    console.log('iOS FCM token received:', token ? `${token.substring(0, 24)}…` : '(empty)');

    if (token) {
      currentToken = token;
      await savePushToken(userId, token);
    } else {
      console.error(
        'iOS FCM token is still empty after retries — push will not work until a token arrives via tokenReceived. ' +
          'Confirm: physical device, Push capability, APNs key in Firebase Console → Cloud Messaging, and that ' +
          '@capacitor/push-notifications is not handling iOS (use Firebase Messaging only on iOS).'
      );
    }

    console.log('iOS push notifications initialized successfully');
  } catch (error) {
    console.error('Error initializing iOS push notifications:', error);
  }
};

const initializeAndroidPushNotifications = async (userId: string): Promise<void> => {
  try {
    const permissionStatus = await PushNotifications.requestPermissions();

    if (permissionStatus.receive === 'granted') {
      await PushNotifications.register();
      setupAndroidPushListeners(userId);
      console.log('Android push notifications initialized successfully');
    } else {
      console.log('Android push notification permission denied');
    }
  } catch (error) {
    console.error('Error initializing Android push notifications:', error);
  }
};

const setupAndroidPushListeners = (userId: string): void => {
  PushNotifications.addListener('registration', async (token: Token) => {
    console.log('Android push registration success, token:', token.value);
    currentToken = token.value;
    await savePushToken(userId, token.value);
  });

  PushNotifications.addListener('registrationError', (error) => {
    console.error('Android push registration error:', error);
  });

  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('Android push notification received:', notification);
    handleForegroundNotification(notification);
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.log('Android push notification action performed:', action);
    handleNotificationTap(action);
  });
};

/**
 * Build a stable doc ID that includes the first 12 chars of the FCM token so that
 * debug builds and production builds (which generate different APNs-environment tokens)
 * each maintain their own Firestore slot. The backend query finds all active tokens for
 * a user regardless of doc ID, so both will receive pushes independently.
 */
function tokenDocId(userId: string, platform: string, token: string): string {
  const tokenSuffix = token.replace(/[^a-zA-Z0-9]/g, '').substring(0, 12);
  return `${userId}_${platform}_${tokenSuffix}`;
}

const savePushToken = async (userId: string, token: string): Promise<void> => {
  if (token === _lastSavedToken) return;
  _lastSavedToken = token;
  try {
    const platform = Capacitor.getPlatform();
    const docId = tokenDocId(userId, platform, token);

    // Use ISO string for cross-platform compatibility (Capacitor drops serverTimestamp)
    await nativeFirebase.setDocument('userPushTokens', docId, {
      userId,
      token,
      platform,
      updatedAt: new Date().toISOString(),
      isActive: true
    }, true);

    console.log('Push token saved to Firestore:', docId, platform);
  } catch (error) {
    console.error('Error saving push token (check auth + Firestore rules for userPushTokens):', error);
  }
};

const handleForegroundNotification = (notification: PushNotificationSchema): void => {
  const { title, body, data } = notification;
  console.log('Foreground notification:', { title, body, data });

  window.dispatchEvent(new CustomEvent('pushNotificationReceived', {
    detail: { title, body, data }
  }));
};

const handleNotificationTap = (action: ActionPerformed): void => {
  const notification = action.notification;
  if (!notification || typeof notification !== 'object') {
    console.warn('🔔 No notification object in tap action');
    return;
  }

  const normalized = normalizeNotificationData(extractTapPayloadForNormalization(notification));
  console.log('🔔 Notification tapped, normalized:', JSON.stringify(normalized));

  const runNavigation = () => {
    console.log('🔔 Executing navigation after notification tap');
    navigateFromNotificationWithFallback(normalized as NotificationData);
  };

  if (!Capacitor.isNativePlatform()) {
    window.setTimeout(runNavigation, 0);
    return;
  }

  void App.getState()
    .then(({ isActive }) => {
      const ms = isActive ? 0 : 150;
      window.setTimeout(runNavigation, ms);
    })
    .catch(() => window.setTimeout(runNavigation, 200));
};

export const getCurrentToken = (): string | null => {
  return currentToken;
};

export const unregisterPushNotifications = async (userId: string): Promise<void> => {
  if (!isPushSupported()) {
    return;
  }

  lastRegisteredNativePushUserId = null;

  if (Capacitor.isNativePlatform()) {
    try {
      if (currentToken) {
        const platform = Capacitor.getPlatform();
        const docId = tokenDocId(userId, platform, currentToken);

        // Use ISO string for cross-platform compatibility
        await nativeFirebase.setDocument('userPushTokens', docId, {
          isActive: false,
          updatedAt: new Date().toISOString()
        }, true);
      }

      if (Capacitor.getPlatform() === 'ios') {
        await FirebaseMessaging.removeAllListeners();
        iosTapListenerAttached = false;
        iosTokenForegroundListenersAttached = false;
      } else {
        await PushNotifications.removeAllListeners();
      }
      currentToken = null;
      console.log('Native push notifications unregistered');
    } catch (error) {
      console.error('Error unregistering native push notifications:', error);
    }
    return;
  }

  try {
    if (webMessaging && currentToken) {
      await deleteToken(webMessaging);
    }
    if (currentToken) {
      const docId = tokenDocId(userId, 'web', currentToken);
      // Use ISO string for consistency
      await nativeFirebase.setDocument('userPushTokens', docId, {
        isActive: false,
        updatedAt: new Date().toISOString()
      }, true);
    }
  } catch (error) {
    console.error('Error unregistering web push:', error);
  } finally {
    webMessaging = null;
    currentToken = null;
    console.log('Web push notifications unregistered');
  }
};

export const checkPushPermission = async (): Promise<'granted' | 'denied' | 'prompt'> => {
  if (Capacitor.isNativePlatform()) {
    try {
      if (Capacitor.getPlatform() === 'ios') {
        const status = await FirebaseMessaging.checkPermissions();
        const receive = status.receive;
        if (receive === 'granted') return 'granted';
        if (receive === 'denied') return 'denied';
        return 'prompt';
      }
      const status = await PushNotifications.checkPermissions();
      const receive = status.receive;
      if (receive === 'granted') return 'granted';
      if (receive === 'denied') return 'denied';
      return 'prompt';
    } catch (error) {
      console.error('Error checking push permission:', error);
      return 'denied';
    }
  }

  if (typeof Notification === 'undefined') {
    return 'denied';
  }
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return 'prompt';
};
